<div class="analysis-container">
    <header class="page-header">
        <div class="header-left">
            <h1>Analysis Dashboard</h1>
            <p class="subtitle">Analyze team performance and project insights</p>
        </div>
        <div class="header-actions">
            <button (click)="showFilters = !showFilters" class="btn-filter-toggle" [class.active]="showFilters">
                <i class="fa" [ngClass]="showFilters ? 'fa-filter' : 'fa-filter'"></i>
                {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
            </button>
        </div>
    </header>

    <!-- Top Controls: Filters -->
    <div class="controls-wrapper" *ngIf="showFilters">

        <!-- Filters Bar -->
        <div class="filters-bar">
            <div class="date-inputs">
                <div class="input-group">
                    <label>From</label>
                    <input type="date" [(ngModel)]="startDate" (ngModelChange)="onDateChange()">
                </div>
                <div class="input-group">
                    <label>To</label>
                    <input type="date" [(ngModel)]="endDate" (ngModelChange)="onDateChange()">
                </div>
            </div>

            <!-- Project Filter -->
            <div class="project-filter">
                <label>Project:</label>
                <select [(ngModel)]="selectedProjectId" (change)="onProjectChange()">
                    <option value="">All Projects</option>
                    <option *ngFor="let p of projects" [value]="p.id">{{ p.name }}</option>
                </select>
            </div>

            <!-- User Filter -->
            <div class="user-filter">
                <label>User:</label>
                <select [(ngModel)]="selectedUserId" (change)="onUserChange()">
                    <option value="">All Users</option>
                    <option *ngFor="let u of users" [value]="u.id">{{ u.firstName }} {{ u.lastName }}</option>
                </select>
            </div>

            <div class="quick-filters">
                <button *ngFor="let option of quickFilterOptions" (click)="setQuickFilter(option.filter)">{{ option.label }}</button>
                <button class="print-btn" (click)="exportPdfReport()"><i class="fa fa-download"></i>Export</button>
            </div>
        </div>

    </div>

    <!-- Content Area: Analysis tabs and Results (together) -->
    <div class="content-wrapper">
        <!-- Secondary Controls: Analysis tabs (always visible) -->
        <div class="secondary-controls">
            <div class="analysis-tabs">
                <div *ngFor="let type of analysisTypes" class="tab-item"
                    [class.active]="selectedAnalysisType === type.id" (click)="selectTab(type.id)">
                    <i class="fa" [ngClass]="type.icon"></i>
                    <span>{{ type.label }}</span>
                </div>
            </div>
        </div>

        <div class="result-area" *ngIf="selectedAnalysisType">

            <div *ngIf="isLoading" class="loading-state">
                <i class="fa fa-spinner fa-spin"></i> Loading...
            </div>

        <div *ngIf="!isLoading && displayData" class="data-display" [ngClass]="animationDirection">

            <!-- Weekly Analysis -->
            <div *ngIf="selectedAnalysisType === 'weekly'">
                <div class="table-responsive">
                    <table class="table">
                        <thead>
                            <tr>
                                <th>Date Range</th>
                                <th>Total Hours</th>
                            </tr>
                        </thead>
                        <tbody>
                            <ng-container *ngFor="let item of displayData">
                                <tr class="main-row clickable-row" (click)="toggleRow(item)">
                                    <td>
                                        <div class="date-range">
                                            <i class="fa fa-chevron-right chevron-icon chevron-main" [class.expanded]="item.expanded"></i>
                                            <div class="range-text">
                                                <span class="date-start">{{ item.startDate | date:'mediumDate' }}</span>
                                                <span class="date-separator">
                                                    <i class="fa fa-chevron-right chevron-icon chevron-mobile" [class.expanded]="item.expanded"></i>
                                                </span>
                                                <span class="date-end">{{ item.endDate | date:'mediumDate' }}</span>
                                            </div>
                                            <span class="week-badge">Week {{ item.weekNumber }}</span>
                                        </div>
                                    </td>
                                    <td><strong>{{ item.totalHours | formatDuration }}</strong></td>
                                </tr>
                                <!-- Mobile: Card-based Breakdown Row -->
                                <tr *ngIf="item.expanded && (item.projectBreakdown?.length > 0 || item.personnelBreakdown?.length > 0)"
                                    class="breakdown-row mobile-only">
                                    <td colspan="2">
                                        <div class="breakdown-content">
                                            <!-- Show User Cards with Project Breakdown -->
                                            <ng-container *ngIf="(selectedUserId || (!selectedUserId && !selectedProjectId)) && item.userBreakdown?.length > 0">
                                                <div class="breakdown-section" *ngFor="let user of item.userBreakdown">
                                                    <div class="breakdown-header clickable-row" (click)="toggleUserCard(item, user); $event.stopPropagation()">
                                                        <span>
                                                            <i class="fa fa-chevron-right chevron-icon" [class.expanded]="isUserCardExpanded(item, user)"></i>
                                                            <i class="fa fa-user"></i> {{ user.userName }}
                                                        </span>
                                                        <span class="header-total">{{ user.totalHours | formatDuration }}</span>
                                                    </div>
                                                    <div class="breakdown-list" *ngIf="isUserCardExpanded(item, user)">
                                                        <div class="breakdown-item" *ngFor="let project of (user.projects || [])">
                                                            <span class="item-name">{{ project.projectName }}</span>
                                                            <span class="item-value">{{ project.totalHours | formatDuration }}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </ng-container>
                                            <!-- Show Personnel Breakdown -->
                                            <div class="breakdown-section"
                                                *ngIf="(!selectedUserId && selectedProjectId) && item.personnelBreakdown?.length > 0">
                                                <div class="breakdown-header">
                                                    <i class="fa fa-users"></i> Personnel
                                                </div>
                                                <div class="breakdown-list">
                                                    <div class="breakdown-item"
                                                        *ngFor="let p of item.personnelBreakdown">
                                                        <span class="item-name">{{ p.userName }}</span>
                                                        <span class="item-value">{{ p.totalHours | formatDuration
                                                            }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <!-- Desktop: Nested Table Rows -->
                                <ng-container *ngIf="item.expanded && (selectedUserId || (!selectedUserId && !selectedProjectId)) && item.userBreakdown?.length > 0">
                                    <ng-container *ngFor="let user of item.userBreakdown">
                                        <tr class="user-row desktop-only clickable-row" (click)="toggleUserCard(item, user); $event.stopPropagation()">
                                            <td class="indented-cell">
                                                <i class="fa fa-chevron-right chevron-icon" [class.expanded]="isUserCardExpanded(item, user)"></i>
                                                <i class="fa fa-user"></i>
                                                {{ user.userName }}
                                            </td>
                                            <td><strong>{{ user.totalHours | formatDuration }}</strong></td>
                                        </tr>
                                        <ng-container *ngIf="isUserCardExpanded(item, user)">
                                            <tr class="project-row desktop-only" *ngFor="let project of (user.projects || [])">
                                                <td class="project-cell">{{ project.projectName }}</td>
                                                <td>{{ project.totalHours | formatDuration }}</td>
                                            </tr>
                                        </ng-container>
                                    </ng-container>
                                </ng-container>
                                <!-- Desktop: Personnel Breakdown Rows -->
                                <ng-container *ngIf="item.expanded && (!selectedUserId && selectedProjectId) && item.personnelBreakdown?.length > 0">
                                    <tr class="user-row desktop-only" *ngFor="let p of item.personnelBreakdown">
                                        <td class="indented-cell">
                                            <i class="fa fa-user"></i>
                                            {{ p.userName }}
                                        </td>
                                        <td><strong>{{ p.totalHours | formatDuration }}</strong></td>
                                    </tr>
                                </ng-container>
                            </ng-container>
                        </tbody>
                    </table>
                </div>
            </div>



            <!-- Monthly Analysis -->
            <div *ngIf="selectedAnalysisType === 'monthly'" class="table-responsive">
                <table class="table">
                    <thead>
                        <tr>
                            <th>Year</th>
                            <th>Month</th>
                            <th>Total Hours</th>
                        </tr>
                    </thead>
                    <tbody>
                        <ng-container *ngFor="let item of displayData">
                            <tr class="main-row clickable-row" (click)="toggleRow(item)">
                                <td>
                                    <i class="fa fa-chevron-right chevron-icon" [class.expanded]="item.expanded"></i>
                                    {{ item.year }}
                                </td>
                                <td>{{ item.monthName }}</td>
                                <td><strong>{{ item.totalHours | formatDuration }}</strong></td>
                            </tr>
                            <!-- Mobile: Card-based Breakdown Row -->
                            <tr *ngIf="item.expanded && (item.userBreakdown?.length > 0 || item.personnelBreakdown?.length > 0)"
                                class="breakdown-row mobile-only">
                                <td colspan="3">
                                    <div class="breakdown-content">
                                        <ng-container
                                            *ngIf="(selectedUserId || (!selectedUserId && !selectedProjectId)) && item.userBreakdown?.length > 0">
                                            <div class="breakdown-section" *ngFor="let user of item.userBreakdown">
                                                <div class="breakdown-header clickable-row" (click)="toggleUserCard(item, user); $event.stopPropagation()">
                                                    <span>
                                                        <i class="fa fa-chevron-right chevron-icon" [class.expanded]="isUserCardExpanded(item, user)"></i>
                                                        <i class="fa fa-user"></i> {{ user.userName }}
                                                    </span>
                                                    <span class="header-total">{{ user.totalHours | formatDuration }}</span>
                                                </div>
                                                <div class="breakdown-list" *ngIf="isUserCardExpanded(item, user)">
                                                    <div class="breakdown-item" *ngFor="let project of (user.projects || [])">
                                                        <span class="item-name">{{ project.projectName }}</span>
                                                        <span class="item-value">{{ project.totalHours | formatDuration }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </ng-container>
                                        <div class="breakdown-section"
                                            *ngIf="(!selectedUserId && selectedProjectId) && item.personnelBreakdown?.length > 0">
                                            <div class="breakdown-header">
                                                <i class="fa fa-users"></i> Personnel
                                            </div>
                                            <div class="breakdown-list">
                                                <div class="breakdown-item"
                                                    *ngFor="let p of item.personnelBreakdown">
                                                    <span class="item-name">{{ p.userName }}</span>
                                                    <span class="item-value">{{ p.totalHours | formatDuration }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <!-- Desktop: Nested Table Rows -->
                            <ng-container *ngIf="item.expanded && (selectedUserId || (!selectedUserId && !selectedProjectId)) && item.userBreakdown?.length > 0">
                                <ng-container *ngFor="let user of item.userBreakdown">
                                    <tr class="user-row desktop-only clickable-row" (click)="toggleUserCard(item, user); $event.stopPropagation()">
                                        <td class="indented-cell" colspan="2">
                                            <i class="fa fa-chevron-right chevron-icon" [class.expanded]="isUserCardExpanded(item, user)"></i>
                                            <i class="fa fa-user"></i>
                                            {{ user.userName }}
                                        </td>
                                        <td><strong>{{ user.totalHours | formatDuration }}</strong></td>
                                    </tr>
                                    <ng-container *ngIf="isUserCardExpanded(item, user)">
                                        <tr class="project-row desktop-only" *ngFor="let project of (user.projects || [])">
                                            <td class="project-cell" colspan="2">{{ project.projectName }}</td>
                                            <td>{{ project.totalHours | formatDuration }}</td>
                                        </tr>
                                    </ng-container>
                                </ng-container>
                            </ng-container>
                            <!-- Desktop: Personnel Breakdown Rows -->
                            <ng-container *ngIf="item.expanded && (!selectedUserId && selectedProjectId) && item.personnelBreakdown?.length > 0">
                                <tr class="user-row desktop-only" *ngFor="let p of item.personnelBreakdown">
                                    <td class="indented-cell" colspan="2">
                                        <i class="fa fa-user"></i>
                                        {{ p.userName }}
                                    </td>
                                    <td><strong>{{ p.totalHours | formatDuration }}</strong></td>
                                </tr>
                            </ng-container>
                        </ng-container>
                    </tbody>
                </table>
            </div>

            <!-- Daily Effort -->
            <div *ngIf="selectedAnalysisType === 'daily'" class="table-responsive">
                <table class="table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Total Hours</th>
                        </tr>
                    </thead>
                    <tbody>
                        <ng-container *ngFor="let item of displayData">
                            <tr class="main-row clickable-row" (click)="toggleRow(item)">
                                <td>
                                    <div class="date-range">
                                        <span class="range-text">
                                            <i class="fa fa-chevron-right chevron-icon" [class.expanded]="item.expanded"></i>
                                            {{ item.date | date:'mediumDate' }}
                                        </span>
                                    </div>
                                </td>
                                <td><strong>{{ item.totalHours | formatDuration }}</strong></td>
                            </tr>
                            <!-- Mobile: Card-based Breakdown Row -->
                            <tr *ngIf="item.expanded && (item.userBreakdown?.length > 0 || item.personnelBreakdown?.length > 0)"
                                class="breakdown-row mobile-only">
                                <td colspan="2">
                                    <div class="breakdown-content">
                                        <ng-container
                                            *ngIf="(selectedUserId || (!selectedUserId && !selectedProjectId)) && item.userBreakdown?.length > 0">
                                            <div class="breakdown-section" *ngFor="let user of item.userBreakdown">
                                                <div class="breakdown-header clickable-row" (click)="toggleUserCard(item, user); $event.stopPropagation()">
                                                    <span>
                                                        <i class="fa fa-chevron-right chevron-icon" [class.expanded]="isUserCardExpanded(item, user)"></i>
                                                        <i class="fa fa-user"></i> {{ user.userName }}
                                                    </span>
                                                    <span class="header-total">{{ user.totalHours | formatDuration }}</span>
                                                </div>
                                                <div class="breakdown-list" *ngIf="isUserCardExpanded(item, user)">
                                                    <div class="breakdown-item" *ngFor="let project of (user.projects || [])">
                                                        <span class="item-name">{{ project.projectName }}</span>
                                                        <span class="item-value">{{ project.totalHours | formatDuration }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </ng-container>
                                        <div class="breakdown-section"
                                            *ngIf="(!selectedUserId && selectedProjectId) && item.personnelBreakdown?.length > 0">
                                            <div class="breakdown-header">
                                                <i class="fa fa-users"></i> Personnel
                                            </div>
                                            <div class="breakdown-list">
                                                <div class="breakdown-item"
                                                    *ngFor="let p of item.personnelBreakdown">
                                                    <span class="item-name">{{ p.userName }}</span>
                                                    <span class="item-value">{{ p.totalHours | formatDuration
                                                        }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <!-- Desktop: Nested Table Rows -->
                            <ng-container *ngIf="item.expanded && (selectedUserId || (!selectedUserId && !selectedProjectId)) && item.userBreakdown?.length > 0">
                                <ng-container *ngFor="let user of item.userBreakdown">
                                    <tr class="user-row desktop-only clickable-row" (click)="toggleUserCard(item, user); $event.stopPropagation()">
                                        <td class="indented-cell">
                                            <i class="fa fa-chevron-right chevron-icon" [class.expanded]="isUserCardExpanded(item, user)"></i>
                                            <i class="fa fa-user"></i>
                                            {{ user.userName }}
                                        </td>
                                        <td><strong>{{ user.totalHours | formatDuration }}</strong></td>
                                    </tr>
                                    <ng-container *ngIf="isUserCardExpanded(item, user)">
                                        <tr class="project-row desktop-only" *ngFor="let project of (user.projects || [])">
                                            <td class="project-cell">{{ project.projectName }}</td>
                                            <td>{{ project.totalHours | formatDuration }}</td>
                                        </tr>
                                    </ng-container>
                                </ng-container>
                            </ng-container>
                            <!-- Desktop: Personnel Breakdown Rows -->
                            <ng-container *ngIf="item.expanded && (!selectedUserId && selectedProjectId) && item.personnelBreakdown?.length > 0">
                                <tr class="user-row desktop-only" *ngFor="let p of item.personnelBreakdown">
                                    <td class="indented-cell">
                                        <i class="fa fa-user"></i>
                                        {{ p.userName }}
                                    </td>
                                    <td><strong>{{ p.totalHours | formatDuration }}</strong></td>
                                </tr>
                            </ng-container>
                        </ng-container>
                    </tbody>
                </table>
            </div>

            <div *ngIf="!isLoading && !displayData" class="no-data">
                <p>No data available</p>
            </div>
        </div>

        <div class="empty-state" *ngIf="!selectedAnalysisType">
            <i class="fa fa-chart-pie"></i>
            <p>Select an analysis type above to view the report.</p>
        </div>
    </div>